import React, { useCallback, useEffect, useRef, useState } from 'react';
import { billingAPI } from '../../services/api';

const MODES = ['test', 'live'];
const CURRENCIES = ['INR', 'USD'];

const titleFor = (field) => ({
  key_id: 'Key ID',
  key_secret: 'Key secret',
  secret_key: 'Secret key',
  client_id: 'Client ID',
  client_secret: 'Client secret',
  merchant_id: 'Merchant ID',
  merchant_key: 'Merchant key',
  merchant_salt: 'Merchant salt',
  webhook_secret: 'Webhook secret',
}[field] || field.replace(/_/g, ' '));

const isIdentifierField = (field) => ['key_id', 'client_id', 'merchant_id'].includes(field);

const CURRENCY_SYMBOL = { INR: '₹', USD: '$' };

// The amount itself comes from the server's diagnostic_amounts; this only
// formats it. Restating the figure here would let the two drift apart.
const chargeLabel = (currency, amounts) => {
  const minor = amounts?.[currency];
  if (!minor) return 'a test payment';
  return `${CURRENCY_SYMBOL[currency] || `${currency} `}${(minor / 100).toFixed(2)}`;
};

const PaymentProvidersPage = () => {
  const [providers, setProviders] = useState({
    available: [], configured: [], credential_fields: {}, credential_groups: {},
  });
  const [drafts, setDrafts] = useState({});
  // Write-only credential drafts live only in component memory. They are
  // cleared after a successful Key Vault write and are never loaded back.
  const [credentialValues, setCredentialValues] = useState({});
  const [savingCards, setSavingCards] = useState({});
  const [cardFeedback, setCardFeedback] = useState({});
  // State alone cannot stop two clicks before React re-renders. This ref is
  // the synchronous lock that guarantees one Key Vault write per card.
  const savingRef = useRef(new Set());
  const [checking, setChecking] = useState({});
  const [checkResult, setCheckResult] = useState({});
  // Which currency a diagnostic charge uses. Defaulting to the account's first
  // enabled currency made a second one untestable.
  const [testCurrencies, setTestCurrencies] = useState({});
  const checkingRef = useRef(new Set());
  const [newProvider, setNewProvider] = useState({
    provider: 'razorpay', display_name: '', credential_key: '',
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const messageTimerRef = useRef(null);

  const flash = useCallback((type, text) => {
    if (messageTimerRef.current) {
      window.clearTimeout(messageTimerRef.current);
      messageTimerRef.current = null;
    }
    setMessage({ type, text });
    // Failures require action and may contain the only useful provider or Key
    // Vault diagnosis. Keep them visible until explicitly dismissed or
    // replaced. Only routine success confirmations expire automatically.
    if (type === 'success') {
      messageTimerRef.current = window.setTimeout(() => {
        setMessage(null);
        messageTimerRef.current = null;
      }, 5000);
    }
  }, []);

  useEffect(() => () => {
    if (messageTimerRef.current) window.clearTimeout(messageTimerRef.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingAPI.getProviders();
      setProviders(response.data || response);
    } catch (error) {
      flash('error', 'Could not load payment providers.');
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const updateDraft = (configId, update) => setDrafts((all) => ({
    ...all,
    [configId]: { ...(all[configId] || {}), ...update },
  }));
  const valueFor = (configId, cfg, key, fallback) => drafts[configId]?.[key] ?? cfg?.[key] ?? fallback;
  const enabledModesFor = (configId, cfg) => valueFor(configId, cfg, 'enabled_modes', cfg?.config?.enabled_modes || {
    test: Boolean(cfg?.is_enabled && cfg?.mode === 'test'),
    live: Boolean(cfg?.is_enabled && cfg?.mode === 'live'),
  });
  const currenciesFor = (configId, cfg) => valueFor(configId, cfg, 'supported_currencies', cfg?.config?.supported_currencies || ['INR']);
  const statusFor = (cfg, mode) => cfg?.credential_status?.[mode] ?? {};
  const enteredValuesFor = (name, mode) => credentialValues[name]?.[mode] || {};
  const setEnteredValue = (name, mode, field, value) => setCredentialValues((all) => ({
    ...all,
    [name]: {
      ...(all[name] || {}),
      [mode]: { ...(all[name]?.[mode] || {}), [field]: value },
    },
  }));
  const clearEnteredValues = (name, mode) => setCredentialValues((all) => ({
    ...all,
    [name]: { ...(all[name] || {}), [mode]: {} },
  }));

  const save = async (cfg, credentialMode) => {
    const configId = cfg.id;
    const name = cfg.provider;
    const cardKey = `${configId}:${credentialMode}`;
    if (savingRef.current.has(cardKey)) return;
    const mode = valueFor(configId, cfg, 'mode', cfg?.mode || 'test');
    const entered = Object.fromEntries(
      Object.entries(enteredValuesFor(configId, credentialMode))
        .filter(([, value]) => value !== ''),
    );
    const incompleteGroup = (providers.credential_groups?.[name] || []).find((group) => {
      const supplied = group.filter((field) => Object.prototype.hasOwnProperty.call(entered, field));
      return supplied.length > 0 && supplied.length !== group.length;
    });
    if (incompleteGroup) {
      flash('error', `Replace ${incompleteGroup.map(titleFor).join(' and ')} together.`);
      return;
    }
    const status = statusFor(cfg, credentialMode);
    const replacements = Object.keys(entered).filter((field) => status[field]);
    if (replacements.length > 0 && !window.confirm(
      `Replace the saved ${replacements.map(titleFor).join(', ')} for ${credentialMode}?`,
    )) return;
    savingRef.current.add(cardKey);
    setSavingCards((all) => ({ ...all, [cardKey]: true }));
    setCardFeedback((all) => ({ ...all, [cardKey]: null }));
    try {
      const payload = {
        display_name: valueFor(configId, cfg, 'display_name', cfg?.display_name || name),
        mode,
        enabled_modes: enabledModesFor(configId, cfg),
        supported_currencies: currenciesFor(configId, cfg),
        credentials_mode: credentialMode,
        ...(Object.keys(entered).length > 0 ? { credential_values: entered } : {}),
      };
      await billingAPI.upsertProvider(configId, payload);
      clearEnteredValues(configId, credentialMode);
      const savedMessage = `Saved ${cfg.display_name || name} ${credentialMode} configuration.`;
      setCardFeedback((all) => ({
        ...all, [cardKey]: { type: 'success', text: savedMessage },
      }));
      await load();
    } catch (error) {
      const failure = `Save failed: ${error?.response?.data?.detail || error.message}`;
      setCardFeedback((all) => ({
        ...all, [cardKey]: { type: 'error', text: failure },
      }));
    } finally {
      savingRef.current.delete(cardKey);
      setSavingCards((all) => ({ ...all, [cardKey]: false }));
    }
  };

  // Both diagnostics share one in-flight lock per card: they hit the same
  // gateway account, and a half-finished check reads as a result.
  const runCheck = async (configId, credentialMode, work) => {
    const cardKey = `${configId}:${credentialMode}`;
    if (checkingRef.current.has(cardKey)) return;
    checkingRef.current.add(cardKey);
    setChecking((all) => ({ ...all, [cardKey]: true }));
    setCheckResult((all) => ({ ...all, [cardKey]: null }));
    try {
      // Awaited before the updater: `await` inside the non-async arrow passed
      // to setCheckResult is a syntax error, not just a lint complaint.
      const outcome = await work();
      setCheckResult((all) => ({ ...all, [cardKey]: outcome }));
    } catch (error) {
      setCheckResult((all) => ({
        ...all,
        [cardKey]: {
          type: 'error',
          text: error?.response?.data?.detail || error.message,
        },
      }));
    } finally {
      checkingRef.current.delete(cardKey);
      setChecking((all) => ({ ...all, [cardKey]: false }));
    }
  };

  const verifyCredentials = (cfg, credentialMode) => runCheck(
    cfg.id, credentialMode, async () => {
      const response = await billingAPI.verifyProviderCredentials(cfg.id, credentialMode);
      const body = response.data || response;
      return { type: body.ok ? 'success' : 'error', text: body.detail };
    },
  );

  const sendTestPayment = (cfg, credentialMode, currency) => {
    if (credentialMode === 'live' && !window.confirm(
      `This creates a REAL ${currency} charge on the live account. `
      + 'Refund it from the gateway dashboard afterwards. Continue?',
    )) return undefined;
    return runCheck(cfg.id, credentialMode, async () => {
      const response = await billingAPI.createProviderTestPayment(
        cfg.id, credentialMode, currency,
      );
      const body = response.data || response;
      return {
        type: 'success',
        text: `Test link created for ${body.currency} ${(body.amount_minor / 100).toFixed(2)}.`,
        url: body.url,
      };
    });
  };

  const activate = async (cfg) => {
    try {
      await billingAPI.activateProvider(cfg.id);
      flash('success', `${cfg.display_name || cfg.provider} is now active for new payments.`);
      load();
    } catch (error) {
      flash('error', `Activation failed: ${error?.response?.data?.detail || error.message}`);
    }
  };

  const remove = async (cfg) => {
    if (!window.confirm(`Delete ${cfg.display_name || cfg.provider}? Existing secret values remain in Key Vault.`)) return;
    try {
      await billingAPI.deleteProvider(cfg.id);
      flash('success', `Deleted ${cfg.display_name || cfg.provider}.`);
      load();
    } catch (error) {
      flash('error', `Delete failed: ${error?.response?.data?.detail || error.message}`);
    }
  };

  const addProvider = async () => {
    if (!newProvider.display_name.trim()) {
      flash('error', 'Give this merchant account a name.');
      return;
    }
    if (!newProvider.credential_key.trim()) {
      flash('error', 'Give this account a shared credential key.');
      return;
    }
    try {
      await billingAPI.createProvider({
        provider: newProvider.provider,
        display_name: newProvider.display_name.trim(),
        credential_key: newProvider.credential_key.trim().toLowerCase(),
      });
      setNewProvider((current) => ({
        ...current, display_name: '', credential_key: '',
      }));
      flash('success', 'Payment-provider account added. Configure credentials before activation.');
      load();
    } catch (error) {
      flash('error', `Add failed: ${error?.response?.data?.detail || error.message}`);
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading payment providers…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-7">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payment Providers</h1>
        <p className="mt-1 text-sm text-gray-600">
          Configure the merchant accounts Serviti can use. Each account has one Test
          credential set and one Live credential set, shared by every environment.
          New values go directly to Azure Key Vault and are never stored in or returned
          by Serviti.
        </p>
      </div>

      {message && (
        <div
          role={message.type === 'error' ? 'alert' : 'status'}
          className={`flex items-start justify-between gap-4 rounded border px-4 py-3 text-sm ${message.type === 'error'
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-green-200 bg-green-50 text-green-700'}`}
        >
          <span className="min-w-0 whitespace-pre-wrap break-words">{message.text}</span>
          <button
            type="button"
            onClick={() => {
              if (messageTimerRef.current) window.clearTimeout(messageTimerRef.current);
              messageTimerRef.current = null;
              setMessage(null);
            }}
            className="flex-shrink-0 rounded px-2 py-0.5 font-medium hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-current"
            aria-label="Dismiss message"
          >
            Dismiss
          </button>
        </div>
      )}

      <section className="rounded-lg border bg-white p-5">
        <h2 className="font-semibold text-gray-900">Add payment-provider account</h2>
        <p className="mt-1 text-sm text-gray-600">
          Add the replacement first, verify it, then make it active. The current
          account remains untouched until activation.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-[180px_1fr_1fr_auto]">
          <select
            value={newProvider.provider}
            onChange={(event) => setNewProvider((current) => ({
              ...current, provider: event.target.value,
            }))}
            className="rounded border px-3 py-2 text-sm"
          >
            {(providers.available || []).filter((name) => name !== 'manual').map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <input
            value={newProvider.display_name}
            onChange={(event) => setNewProvider((current) => ({
              ...current, display_name: event.target.value,
            }))}
            placeholder="Account name, e.g. Razorpay India replacement"
            className="rounded border px-3 py-2 text-sm"
          />
          <input
            value={newProvider.credential_key}
            onChange={(event) => setNewProvider((current) => ({
              ...current, credential_key: event.target.value.toLowerCase(),
            }))}
            placeholder="Shared key, e.g. razorpay-primary"
            aria-label="Shared credential key"
            className="rounded border px-3 py-2 font-mono text-sm"
          />
          <button onClick={addProvider} className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700">
            Add account
          </button>
        </div>
      </section>

      {(providers.configured || []).map((cfg) => {
        const name = cfg.provider;
        const configId = cfg.id;
        const selectedMode = valueFor(configId, cfg, 'mode', cfg.mode || 'test');
        const enabledModes = enabledModesFor(configId, cfg);
        const currencies = currenciesFor(configId, cfg);
        const fields = providers.credential_fields?.[name] || [];

        if (name === 'manual') {
          return (
            <section key={configId} className="rounded-lg border bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-gray-900">Manual</h2>
                  <p className="mt-1 text-sm text-gray-600">Emergency checkout kill-switch. No provider credentials or webhooks.</p>
                </div>
                {!cfg.is_active && <button onClick={() => activate(cfg)} className="rounded bg-gray-800 px-3 py-2 text-sm text-white hover:bg-gray-900">Disable online checkout</button>}
                {cfg.is_active && <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">online checkout disabled</span>}
              </div>
            </section>
          );
        }

        return (
          <section key={configId} className={`rounded-lg border bg-white p-5 space-y-5 ${cfg.is_active ? 'ring-2 ring-green-500' : ''}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-gray-900">{cfg.display_name || name}</h2>
                  <span className="rounded bg-gray-100 px-2 py-1 text-xs uppercase text-gray-700">{name}</span>
                  {cfg.is_active && <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">active for new payments</span>}
                  {!cfg.is_active && <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">inactive</span>}
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Enter new credentials only when setting up or rotating this provider.
                  Saved values cannot be viewed again.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Shared credential key: <code>{cfg.credential_key}</code>
                </p>
              </div>
              <div className="flex gap-2">
                {!cfg.is_active && <button onClick={() => activate(cfg)} className="rounded bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700">Make active</button>}
                {!cfg.is_active && <button onClick={() => remove(cfg)} className="rounded border px-3 py-2 text-sm text-red-700 hover:bg-red-50">Delete</button>}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm">
                <span className="text-gray-700">Payment provider name</span>
                <input
                  value={valueFor(configId, cfg, 'display_name', cfg.display_name || name)}
                  onChange={(event) => updateDraft(configId, { display_name: event.target.value })}
                  className="mt-1 w-full rounded border px-2 py-1.5"
                />
              </label>
              <label className="text-sm">
                <span className="text-gray-700">Checkout mode</span>
                <select
                  value={selectedMode}
                  onChange={(event) => updateDraft(configId, { mode: event.target.value })}
                  className="mt-1 w-full rounded border px-2 py-1.5"
                >
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </select>
                <span className="mt-1 block text-xs text-gray-500">Save either card before activating a changed mode.</span>
              </label>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-800">Supported checkout currencies</h3>
              <div className="mt-2 flex gap-4">
                {CURRENCIES.map((currency) => (
                  <label key={currency} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={currencies.includes(currency)}
                      onChange={(event) => {
                        const next = event.target.checked
                          ? [...new Set([...currencies, currency])]
                          : currencies.filter((item) => item !== currency);
                        updateDraft(configId, { supported_currencies: next });
                      }}
                    />
                    {currency}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">USD is refused at checkout until it is explicitly enabled here.</p>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {MODES.map((credentialMode) => {
                const status = statusFor(cfg, credentialMode);
                const entered = enteredValuesFor(configId, credentialMode);
                const enabled = Boolean(enabledModes[credentialMode]);
                const cardKey = `${configId}:${credentialMode}`;
                const isSaving = Boolean(savingCards[cardKey]);
                const feedback = cardFeedback[cardKey];
                const isChecking = Boolean(checking[cardKey]);
                const checkOutcome = checkResult[cardKey];
                const testCurrency = testCurrencies[cardKey] || currencies[0] || 'INR';
                const testCharge = chargeLabel(
                  testCurrency, providers.diagnostic_amounts,
                );
                return (
                  <div
                    key={credentialMode}
                    data-testid={`credentials-${configId}-${credentialMode}`}
                    className="rounded border bg-gray-50 p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <h3 className="font-medium capitalize text-gray-900">{credentialMode} credentials</h3>
                        <p className="text-xs text-gray-500">Shared across dev, staging and production.</p>
                      </div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(event) => updateDraft(configId, {
                            enabled_modes: { ...enabledModes, [credentialMode]: event.target.checked },
                          })}
                        />
                        Enabled
                      </label>
                    </div>
                    {fields.map((field) => (
                      <label key={field} className="block text-sm">
                        <span className="text-gray-700">{titleFor(field)}</span>
                        <input
                          type={isIdentifierField(field) ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder={status[field]
                            ? 'Leave blank to keep the configured value'
                            : isIdentifierField(field)
                              ? `Enter the ${credentialMode} ${titleFor(field)}`
                              : `Enter the ${credentialMode} ${titleFor(field).toLowerCase()}`}
                          value={entered[field] || ''}
                          onChange={(event) => setEnteredValue(
                            configId, credentialMode, field, event.target.value,
                          )}
                          className="mt-1 w-full rounded border px-2 py-1.5 font-mono text-xs"
                        />
                        <span className={`mt-1 block text-xs ${status[field] ? 'text-green-700' : 'text-amber-700'}`}>
                          {status[field]
                            ? 'Configured in Key Vault. Enter a value only to replace it.'
                            : 'Not configured. Enter a value before activating this mode.'}
                        </span>
                      </label>
                    ))}
                    {(providers.credential_groups?.[name] || []).map((group) => (
                      <p key={group.join('-')} className="text-xs text-gray-600">
                        {group.map(titleFor).join(' and ')} must be replaced together.
                      </p>
                    ))}
                    <button
                      onClick={() => save(cfg, credentialMode)}
                      disabled={isSaving}
                      className="rounded bg-gray-800 px-3 py-2 text-sm text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSaving ? 'Saving…' : `Save ${credentialMode} configuration`}
                    </button>
                    {feedback && (
                      <p
                        role="status"
                        className={`text-sm ${feedback.type === 'error' ? 'text-red-700' : 'text-green-700'}`}
                      >
                        {feedback.text}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                      <button
                        onClick={() => verifyCredentials(cfg, credentialMode)}
                        disabled={isChecking}
                        data-testid={`verify-${configId}-${credentialMode}`}
                        className="rounded border border-gray-400 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isChecking ? 'Checking…' : 'Verify credentials'}
                      </button>
                      {currencies.length > 1 && (
                        <select
                          value={testCurrency}
                          aria-label={`Test payment currency for ${credentialMode}`}
                          onChange={(event) => setTestCurrencies((all) => ({
                            ...all, [cardKey]: event.target.value,
                          }))}
                          className="rounded border border-gray-400 px-2 py-2 text-sm text-gray-800"
                        >
                          {currencies.map((code) => (
                            <option key={code} value={code}>{code}</option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => sendTestPayment(cfg, credentialMode, testCurrency)}
                        disabled={isChecking}
                        data-testid={`test-payment-${configId}-${credentialMode}`}
                        className="rounded border border-gray-400 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {`Send ${testCharge} test payment`}
                      </button>
                      <span className="text-xs text-gray-500">
                        {credentialMode === 'live'
                          ? 'Verifying is free. The test payment is a real charge — refund it in the dashboard.'
                          : 'Verifying is free. The test payment uses gateway test mode — no real money.'}
                      </span>
                    </div>
                    {checkOutcome && (
                      <p
                        role="status"
                        className={`text-sm ${checkOutcome.type === 'error' ? 'text-red-700' : 'text-green-700'}`}
                      >
                        {checkOutcome.text}
                        {checkOutcome.url && (
                          <a
                            href={checkOutcome.url}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 underline"
                          >
                            Open the payment page
                          </a>
                        )}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default PaymentProvidersPage;
